<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  botId: { type: Number, required: true },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const activeStatusFilter = ref('active');

const statusFilters = [
  { key: 'active', icon: 'i-lucide-circle-dot', color: 'teal' },
  { key: 'acknowledged', icon: 'i-lucide-eye', color: 'amber' },
  { key: 'resolved', icon: 'i-lucide-check-circle', color: 'slate' },
  { key: 'archived', icon: 'i-lucide-archive', color: 'slate' },
];

const conversationPath = displayId => {
  if (!displayId) return '';
  const route = router.resolve({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: displayId,
    },
  });
  return route.href;
};

const recommendations = computed(
  () => store.getters['wgptBotEvalRecommendations/getRecommendations']
);
const uiFlags = computed(
  () => store.getters['wgptBotEvalRecommendations/getUIFlags']
);

const fetchRecommendations = (status = activeStatusFilter.value) => {
  store.dispatch('wgptBotEvalRecommendations/get', {
    botId: props.botId,
    status,
  });
};

const setStatusFilter = status => {
  activeStatusFilter.value = status;
  fetchRecommendations(status);
};

const priorityConfig = {
  critical: {
    color: 'text-n-ruby-11',
    badge: 'bg-n-ruby-3 text-n-ruby-11',
    icon: 'i-lucide-alert-circle',
    border: 'border-n-ruby-6',
  },
  high: {
    color: 'text-n-amber-11',
    badge: 'bg-n-amber-3 text-n-amber-11',
    icon: 'i-lucide-alert-triangle',
    border: 'border-n-amber-6',
  },
  medium: {
    color: 'text-n-slate-12',
    badge: 'bg-n-alpha-2 text-n-slate-11',
    icon: 'i-lucide-info',
    border: 'border-n-weak',
  },
  low: {
    color: 'text-n-slate-11',
    badge: 'bg-n-alpha-2 text-n-slate-10',
    icon: 'i-lucide-minus',
    border: 'border-n-weak',
  },
};

const getPriorityConfig = priority =>
  priorityConfig[priority] || priorityConfig.medium;

const statusBadgeClass = status => {
  const map = {
    active: 'bg-n-teal-3 text-n-teal-11',
    acknowledged: 'bg-n-amber-3 text-n-amber-11',
    resolved: 'bg-n-alpha-2 text-n-slate-11',
    archived: 'bg-n-alpha-2 text-n-slate-10',
  };
  return map[status] || map.active;
};

const updateStatus = async (recommendation, newStatus) => {
  try {
    await store.dispatch('wgptBotEvalRecommendations/update', {
      botId: props.botId,
      id: recommendation.id,
      status: newStatus,
    });
    useAlert(
      t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.API.UPDATE_SUCCESS')
    );
    fetchRecommendations();
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.API.ERROR')
    );
  }
};

onMounted(() => {
  fetchRecommendations();
});
</script>

<template>
  <div class="flex flex-col gap-4 pb-8">
    <p class="text-sm text-n-slate-11">
      {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.DESCRIPTION') }}
    </p>

    <div class="flex items-center gap-2">
      <Button
        v-for="filter in statusFilters"
        :key="filter.key"
        :label="
          // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
          t(
            `WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.FILTER_${filter.key.toUpperCase()}`
          )
        "
        :variant="activeStatusFilter === filter.key ? 'solid' : 'faded'"
        :color="activeStatusFilter === filter.key ? filter.color : 'slate'"
        size="sm"
        :icon="filter.icon"
        @click="setStatusFilter(filter.key)"
      />
    </div>

    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-16"
    >
      <Spinner />
    </div>

    <template v-else>
      <div
        v-if="!recommendations.length"
        class="flex flex-col items-center justify-center py-16 gap-2"
      >
        <Icon icon="i-lucide-lightbulb" class="size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-11">
          {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.EMPTY') }}
        </p>
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="rec in recommendations"
          :key="rec.id"
          class="rounded-xl border p-4"
          :class="getPriorityConfig(rec.priority).border"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-3 flex-1 min-w-0">
              <div
                class="size-9 flex-shrink-0 rounded-lg flex items-center justify-center"
                :class="getPriorityConfig(rec.priority).badge"
              >
                <Icon
                  :icon="getPriorityConfig(rec.priority).icon"
                  class="size-4"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase"
                    :class="getPriorityConfig(rec.priority).badge"
                  >
                    {{ rec.priority }}
                  </span>
                  <span
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium capitalize"
                    :class="statusBadgeClass(rec.status)"
                  >
                    {{ rec.status }}
                  </span>
                </div>

                <h4
                  class="text-sm font-medium mb-1"
                  :class="getPriorityConfig(rec.priority).color"
                >
                  {{ rec.title }}
                </h4>
                <p class="text-xs text-n-slate-11 whitespace-pre-line">
                  {{ rec.description }}
                </p>

                <div
                  v-if="rec.conversations?.length"
                  class="mt-3 flex items-center gap-2"
                >
                  <span
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{
                      t(
                        'WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.RELATED_CONVERSATIONS',
                        { count: rec.conversations.length }
                      )
                    }}
                  </span>
                  <div class="flex flex-wrap gap-1.5">
                    <a
                      v-for="conv in rec.conversations"
                      :key="conv.display_id"
                      :href="conversationPath(conv.display_id)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-xs text-n-brand hover:underline"
                    >
                      {{ `#${conv.display_id}` }}
                    </a>
                  </div>
                </div>

                <div
                  v-if="rec.period_start && rec.period_end"
                  class="mt-2 text-[10px] text-n-slate-10"
                >
                  {{
                    t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.PERIOD', {
                      start: rec.period_start,
                      end: rec.period_end,
                    })
                  }}
                </div>
              </div>
            </div>

            <div
              v-if="rec.status === 'active'"
              class="flex items-center gap-2 flex-shrink-0"
            >
              <Button
                :label="
                  t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.ACKNOWLEDGE')
                "
                variant="faded"
                color="slate"
                size="xs"
                :is-loading="uiFlags.isUpdating"
                @click="updateStatus(rec, 'acknowledged')"
              />
              <Button
                :label="
                  t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.RESOLVE')
                "
                variant="faded"
                color="slate"
                size="xs"
                icon="i-lucide-check"
                :is-loading="uiFlags.isUpdating"
                @click="updateStatus(rec, 'resolved')"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
